import React from "react";
import { FormDataFilter } from "../../schemas/DemoTypes";
import { IBaseControlProps } from "./Utils";
import { ControlledBooleanFilter } from "./BooleanFilter";
import { ControlledStringFilter } from "./StringFilter";
import { ControlledNumberFilter } from "./NumberFilter";
import { ControlledDateRangeFilter } from "./DateRangeFilter";
import { ControlledDropdownFilter } from "./DropdownFilter";
import { ControlledLookUpFilter } from "./LookUpFilter";
import { UseFormMethods } from "react-hook-form";
import { GetLookUpOptionsParamsType, LookUpOptionsType } from "../../queries/getCrmLookUpOptions";

interface IFilterFormProps extends Omit<IBaseControlProps, "name"> {
  filter: Partial<FormDataFilter>;
  setError: UseFormMethods["setError"];
  index: number;
  className?: string;
  lookUpOptionsFunction: (
    params: GetLookUpOptionsParamsType,
    enabled: boolean,
  ) => {
    isLoading: boolean;
    data:
      | {
          options: LookUpOptionsType[];
        }
      | undefined;
  };
}

export const FilterForm = ({
  control,
  errors,
  filter,
  setError,
  index,
  className,
  lookUpOptionsFunction,
}: IFilterFormProps) => {
  if (!filter.label || !filter.field) {
    throw new Error("Filter must have a label");
  }

  const name = `filters.${index}.filter`;
  switch (filter.type) {
    case "Address":
    case "Email":
    case "PhoneNumber":
    case "Text":
    case "TextArea":
    case "URL":
      return (
        <ControlledStringFilter
          className={className}
          control={control}
          name={name}
          errors={errors}
          label={filter.label}
          defaultValues={filter.filter?.jsonValue}
          defaultOperator={filter.filter?.stringCondition}
        />
      );
    case "Number":
    case "Percent":
    case "Currency":
      return (
        <ControlledNumberFilter
          className={className}
          control={control}
          name={name}
          errors={errors}
          label={filter.label}
          numberFieldType={filter.type}
          defaultValues={filter.filter?.numberValue}
          defaultOperator={filter.filter?.numberCondition}
        />
      );
    case "Checkbox":
      return (
        <ControlledBooleanFilter
          className={className}
          control={control}
          name={name}
          errors={errors}
          label={filter.label}
          defaultOperator={filter.filter?.booleanCondition}
        />
      );
    case "Date":
    case "DateTime":
    case "Time":
      return (
        <ControlledDateRangeFilter
          className={className}
          control={control}
          name={name}
          errors={errors}
          label={filter.label}
          setError={setError}
          defaultDateOption={filter.filter?.dateValue?.dateOption}
          defaultStartDate={filter.filter?.dateValue?.startDate}
          defaultEndDate={filter.filter?.dateValue?.endDate}
          defaultOperator={filter.filter?.dateCondition}
        />
      );
    case "Dropdown":
    case "DropdownMultiSelect":
      return (
        <ControlledDropdownFilter
          className={className}
          control={control}
          name={name}
          errors={errors}
          label={filter.label}
          options={filter.picklist || []}
          defaultOperator={filter.filter?.dropdownCondition}
          defaultValues={filter.filter?.jsonValue}
        />
      );
    case "LookUp":
      if (!filter.lookUpType) {
        throw new Error("LookUp filter requires a lookUpType");
      }
      return (
        <ControlledLookUpFilter
          className={className}
          control={control}
          name={name}
          errors={errors}
          label={filter.label}
          lookUpType={filter.lookUpType}
          lookUpField={filter.field}
          lookUpNeedsSearch={!!filter.lookUpNeedsSearch}
          lookUpOptionsFunction={lookUpOptionsFunction}
          defaultValues={filter.filter?.jsonValue}
          defaultOperator={filter.filter?.dropdownCondition}
        />
      );
    default:
      throw new Error(`Unsupported filter type: ${filter.type}`);
  }
};
